import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Product, ProductUpdate } from '@domain/product/models/Product';
import { PaginatedResource } from '@domain/base/Resource';
import { AddProductPartDTO } from '@infrastructure/product/dto/AddProductPartDTO';

export const useProductUpdate = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: updateProduct } = useMutation({
        mutationFn: async ({ productId, updateData }: { productId: string, updateData: ProductUpdate }) => {
            const result = await container.productRepository.updateProduct(productId, updateData);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (updatedProduct) => {
            // Update the specific product query cache
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.setQueryData(['product', updatedProduct.id], updatedProduct);

            // Update all products list caches optimistically
            queryClient.setQueriesData(
                { queryKey: ['products'] },
                (oldData: PaginatedResource<Product[]> | undefined) => {
                    if (!oldData || oldData.status !== 'success') return oldData;

                    return {
                        ...oldData,
                        data: oldData.data.map(product =>
                            product.id === updatedProduct.id ? updatedProduct : product
                        )
                    };
                }
            );
        },
    });

    const { mutateAsync: removeProductPart, isPending: isRemovingProductPart } = useMutation({
        mutationFn: async ({ productId, partId }: { productId: string, partId: string }) => {
            const result = await container.productRepository.removeProductPart(productId, partId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        }
    });

    const { mutateAsync: addProductPart, isPending: isAddingProductPart } = useMutation({
        mutationFn: async ({ productId, data }: { productId: string, data: AddProductPartDTO[] }) => {
            const result = await container.productRepository.addProductParts(productId, data);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        }
    });

    const { mutateAsync: addProductUsedIn, isPending: isAddingProductUsedIn } = useMutation({
        mutationFn: async ({ productId, data }: { productId: string, data: AddProductPartDTO[] }) => {
            const result = await container.productRepository.addProductUsedIn(productId, data);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        }
    });

    const { mutateAsync: removeProductUsedIn, isPending: isRemovingProductUsedIn } = useMutation({
        mutationFn: async ({ productId, componentId }: { productId: string, componentId: string }) => {
            const result = await container.productRepository.removeProductUsedIn(productId, componentId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        }
    });

    return {
        updateProduct,
        removeProductPart,
        isRemovingProductPart,
        addProductPart,
        isAddingProductPart,
        addProductUsedIn,
        isAddingProductUsedIn,
        removeProductUsedIn,
        isRemovingProductUsedIn,
    };
};
